import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/site/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";

export default function Register() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [form, setForm] = useState({});
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    useSeo({ title: "Create Account | SUDIGI" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            let data;
            if (form.role === "organiser") {
                ({ data } = await api.post("/organiser/register", { name: form.name, email: form.email, password: form.password, phone: form.phone || "", organisation: form.organisation || form.name }));
            } else {
                ({ data } = await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, phone: form.phone || "" }));
            }
            login(data.token, data.user);
            nav(data.user.role === "organiser" ? "/organiser" : "/account");
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Registration failed"); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-950 via-teal-900 to-teal-800 grain grid place-items-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-6"><Logo /></div>
                <div className="card-teal rounded-2xl p-8">
                    <h1 className="font-serif text-2xl text-cream mb-1">Create your account</h1>
                    <p className="text-cream-soft text-sm mb-6">Track your orders and project status.</p>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                            {[["customer", "Join as Brand"], ["organiser", "Join as Organiser"]].map(([r, label]) => (
                                <button type="button" key={r} onClick={() => setForm((f) => ({ ...f, role: r }))} className={`rounded-xl py-2.5 text-sm font-semibold border transition-colors ${(form.role || "customer") === r ? "bg-gold text-teal-900 border-gold" : "border-brandborder text-cream-soft"}`} data-testid={`register-role-${r}`}>{label}</button>
                            ))}
                        </div>
                        <div className="space-y-1.5"><Label className="text-cream-soft text-sm">Name</Label><Input className="bg-teal-800/50 border-brandborder text-cream" value={form.name || ""} onChange={set("name")} data-testid="register-name" required /></div>
                        <div className="space-y-1.5"><Label className="text-cream-soft text-sm">Email</Label><Input type="email" className="bg-teal-800/50 border-brandborder text-cream" value={form.email || ""} onChange={set("email")} data-testid="register-email" required /></div>
                        <div className="space-y-1.5"><Label className="text-cream-soft text-sm">Phone</Label><Input className="bg-teal-800/50 border-brandborder text-cream" value={form.phone || ""} onChange={set("phone")} data-testid="register-phone" /></div>
                        {form.role === "organiser" && <div className="space-y-1.5"><Label className="text-cream-soft text-sm">Organisation</Label><Input className="bg-teal-800/50 border-brandborder text-cream" value={form.organisation || ""} onChange={set("organisation")} data-testid="register-organisation" /></div>}
                        <div className="space-y-1.5"><Label className="text-cream-soft text-sm">Password</Label><Input type="password" className="bg-teal-800/50 border-brandborder text-cream" value={form.password || ""} onChange={set("password")} data-testid="register-password" required /></div>
                        {err && <p className="text-sm text-red-400" data-testid="register-error">{err}</p>}
                        <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-3 flex items-center justify-center gap-2" data-testid="register-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Account</button>
                    </form>
                    <p className="text-sm text-cream-soft mt-5 text-center">Already have an account? <Link to="/login" className="text-gold hover:underline">Login</Link></p>
                </div>
            </div>
        </div>
    );
}
